import { useRef } from "react";

const MINZOOMLEVEL = 1;
const MAXZOOMLEVEL = 5;
const PANAMOUNT = 0.25;
const PANMARGIN = 0.9;

/**
 * Custom hook for managing image zoom and pan functionality
 * Provides zoom in/out, pan controls, and keyboard/mouse interactions
 */
export const useImageZoom = () => {
  const activeImageRef = useRef();
  const imageWrapperRef = useRef();
  
  // Use refs for mutable values that don't trigger re-renders
  const zoomLevelRef = useRef(MINZOOMLEVEL);
  const currentImageXRef = useRef(0);
  const currentImageYRef = useRef(0);
  const draggingRef = useRef(false);
  const imageDragStartXRef = useRef(0);
  const imageDragStartYRef = useRef(0);

  /**
   * Apply transform to image element
   */
  const transformImage = () => {
    if (activeImageRef?.current) {
      const x = currentImageXRef.current;
      const y = currentImageYRef.current;
      const zoom = zoomLevelRef.current;
      activeImageRef.current.style.transform = `translate(${x}px, ${y}px) scale(${zoom})`;
    }
  };

  /**
   * Apply zoom level constraints and update transform
   */
  const imageZoom = () => {
    if (zoomLevelRef.current > MAXZOOMLEVEL) {
      zoomLevelRef.current = MAXZOOMLEVEL;
    } else if (zoomLevelRef.current < MINZOOMLEVEL) {
      zoomLevelRef.current = MINZOOMLEVEL;
    }
    transformImage();
  };

  /**
   * Pan image to specific coordinates
   */
  const imagePan = (x, y) => {
    currentImageXRef.current = x;
    currentImageYRef.current = y;
    transformImage();
  };

  /**
   * Pan image in a specific direction with bounds checking
   */
  const imagePanDirected = (direction, value) => {
    if (activeImageRef?.current) {
      const img = activeImageRef.current;
      let newX = currentImageXRef.current;
      let newY = currentImageYRef.current;
      const r = img.getBoundingClientRect();
      const zoom = zoomLevelRef.current;

      if (direction === "x") {
        newX = currentImageXRef.current + r.width * value;
        const maxPan = r.width * zoom * PANMARGIN;
        if (newX < -maxPan) {
          newX = -maxPan;
        } else if (newX > maxPan) {
          newX = maxPan;
        }
      } else if (direction === "y") {
        newY = currentImageYRef.current + r.height * value;
        const maxPan = r.height * zoom * PANMARGIN;
        if (newY < -maxPan) {
          newY = -maxPan;
        } else if (newY > maxPan) {
          newY = maxPan;
        }
      }
      imagePan(newX, newY);
    }
  };

  /**
   * Zoom controls
   */
  const imageZoomIn = () => {
    zoomLevelRef.current++;
    imageZoom();
  };

  const imageZoomOut = () => {
    zoomLevelRef.current--;
    imageZoom();
  };

  /**
   * Pan controls
   */
  const imagePanRight = () => imagePanDirected("x", PANAMOUNT);
  const imagePanLeft = () => imagePanDirected("x", -1 * PANAMOUNT);
  const imagePanUp = () => imagePanDirected("y", -1 * PANAMOUNT);
  const imagePanDown = () => imagePanDirected("y", PANAMOUNT);

  /**
   * Keyboard event handlers
   */
  const handleKeyDown = (event) => {
    // Disable default action for arrow keys
    switch (event.keyCode) {
      case 37: // left arrow
      case 39: // right arrow
      case 38: // up arrow
      case 40: // down arrow
        event.preventDefault();
        return false;
      default:
        break;
    }
  };

  const handleKeyUp = (event) => {
    switch (event.keyCode) {
      case 37: // left arrow
        imagePanLeft();
        break;
      case 39: // right arrow
        imagePanRight();
        break;
      case 38: // up arrow
        imagePanUp();
        break;
      case 40: // down arrow
        imagePanDown();
        break;
      case 187: // chrome +
      case 61: // firefox +
        imageZoomIn();
        break;
      case 189: // chrome -
      case 173: // firefox -
        imageZoomOut();
        break;
      default:
        break;
    }
  };

  /**
   * Mouse drag handlers
   */
  const handleDragStart = (event) => {
    if (event.type !== "touchstart") {
      event.preventDefault();
    }
    draggingRef.current = true;
    const img = activeImageRef.current;
    if (img) {
      imageDragStartXRef.current = window.event.clientX - currentImageXRef.current;
      imageDragStartYRef.current = window.event.clientY - currentImageYRef.current;
      img.classList.add("notransition");
    }
  };

  const handleDragEnd = () => {
    const img = activeImageRef.current;
    if (img) {
      img.classList.remove("notransition");
    }
    draggingRef.current = false;
  };

  const handleDragging = () => {
    if (draggingRef.current) {
      imagePan(
        window.event.clientX - imageDragStartXRef.current,
        window.event.clientY - imageDragStartYRef.current
      );
    }
  };

  return {
    activeImageRef,
    imageWrapperRef,
    imageZoomIn,
    imageZoomOut,
    imagePanLeft,
    imagePanRight,
    imagePanUp,
    imagePanDown,
    handleKeyDown,
    handleKeyUp,
    handleDragStart,
    handleDragEnd,
    handleDragging,
  };
};
